import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { VideosDataService, VideosColumnsService } from '../../services';
import { DialogVideoFormComponent } from './video-form/video-form.component';
import { DialogVideoDetailsComponent } from './video-details/video-details.component';
import { DialogVideoConverterComponent } from './video-converter/video-converter.component';
import { Video, VideosResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-videos',
  templateUrl: './videos.component.html',
  styleUrls: ['./videos.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class VideosComponent implements OnInit, OnDestroy {

  private videoAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private videoEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private videoDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  private videoConverterDialog: DynamicDialogRef = new DynamicDialogRef();
  public videoToDelete: Video = {};
  public videos: Video[] = [];
  public video: Video = {};
  public selectedVideos: Video[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private videosDataService: VideosDataService,
    private videosColumnsService: VideosColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('VideosComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Videos - Management');
    this.primengConfig.ripple = true;
    this.getVideos();
  }

  ngOnDestroy(): void {
    console.log('VideosComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getVideos(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.videosDataService.getVideos(this.parameters).subscribe((response: VideosResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.videos = response.results;
        } else {
          this.videos = this.videos.concat(response.results);
        }
        if (response.inlineCount > this.videos.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getVideos();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOSNOTLOADED') });
    });
  }

  importVideos(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.videosDataService.addVideos(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('VIDEOSCREATED') });
                if (proceed === j) {
                  this.getVideos();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOSNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOSNOTCREATED') + ' ' + msg });
            });
          } else {
            this.videosDataService.updateVideos(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('VIDEOSUPDATED') });
                if (proceed === j) {
                  this.getVideos();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOSNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOSNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.videosColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('videos.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedVideos() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Videos?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.videosDataService.deleteVideos(this.selectedVideos).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Videos Deleted', life: 3000 });
            this.getVideos();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Videos not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Videos not deleted' });
        });
        this.selectedVideos = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.videos, this.columns, 'Videos');
  }

  exportExcel() {
    ToolsService.exportExcel(this.videos, this.columns, 'Videos');
  }

  exportCsv() {
    ToolsService.exportCsv(this.videos, this.columns, 'Videos');
  }

  refresh() {
    this.videosDataService.clearCache();
    this.getVideos();
  }

  videoAdd(): void {
    if (!this.navigation.getParamValue('videos.addInDialog', true)) {
      this.router.navigate(['/videos/add']);
      return;
    }
    this.videoAddDialog = this.dialogService.open(DialogVideoFormComponent, {
      header: '', // this.translate.instant('NEWVIDEO'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        video: { id: null },
        categories: this.videosColumnsService.categories,
        subCategories: this.videosColumnsService.subCategories,
        experts: this.videosColumnsService.experts,
        videoTypes: this.videosColumnsService.videoTypes,
        prices: this.videosColumnsService.prices,
        sharings: this.videosColumnsService.sharings,
        shows: this.videosColumnsService.shows,
        users: this.videosColumnsService.users,
      },
    });

    this.videoAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Video added' });
        this.getVideos();
      }
    });
  }

  videoEdit(video: Video): void {
    if (!this.navigation.getParamValue('videos.editInDialog', true)) {
      this.router.navigate(['/videos/edit/' + video.id]);
      return;
    }
    this.videoEditDialog = this.dialogService.open(DialogVideoFormComponent, {
      header: '', // this.translate.instant('EDITVIDEO') + ' ' + video.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        video: video,
        categories: this.videosColumnsService.categories,
        subCategories: this.videosColumnsService.subCategories,
        experts: this.videosColumnsService.experts,
        videoTypes: this.videosColumnsService.videoTypes,
        prices: this.videosColumnsService.prices,
        sharings: this.videosColumnsService.sharings,
        shows: this.videosColumnsService.shows,
        users: this.videosColumnsService.users,
      },
    });

    this.videoEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Video updated' });
        this.getVideos();
      }
    });
  }

  videoDetails(video: Video): void {
    if (!this.navigation.getParamValue('videos.detailsInDialog', true)) {
      this.router.navigate(['/videos/details/' + video.id]);
      return;
    }
    this.videoDetailsDialog = this.dialogService.open(DialogVideoDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        video: video,
      }
    });
  }

  videoDelete(video: Video): void {
    this.videoToDelete = video;
    this.messageService.clear();
    this.messageService.add({
      key: 'videoDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + video.name + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.videosDataService.deleteVideo(this.videoToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Video deleted' });
      this.getVideos();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Video not deleted' });
    });
  }

  onDeleteReject(): void {
    this.videoToDelete = {};
    this.messageService.clear('videoDelete');
  }
  videoConverter(video: Video): void {
    if (!this.navigation.getParamValue('videos.converterInDialog', true)) {
      this.router.navigate(['/videos/converter/' + video.id]);
      return;
    }
    this.videoConverterDialog = this.dialogService.open(DialogVideoConverterComponent, {
      header: '', // this.translate.instant('CONVERTVIDEO') + ' ' + video.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        video: video,
      },
    });

    this.videoConverterDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Video converted' });
        this.getVideos();
      }
    });
  }

}
